<template>
  <div class="notify-me">
    <div v-if="!success" class="notify-me__form">
      <p
        class="notify-me__info text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-html="$string('product.notify_me.info_html')"
      />

      <div class="notify-me__form form-group">
        <label
          class="form-group__label visually-hidden"
          for="notify-me-email"
          v-text="$string('product.notify_me.email.label')"
        />

        <input
          id="notify-me-email"
          ref="input"
          v-model="email"
          class="notify-me__input"
          :class="{
            [$class('error')]: state === 'invalid' || state === 'error',
          }"
          autocapitalize="off"
          autocorrect="off"
          name="email"
          :placeholder="$string('product.notify_me.email.placeholder')"
          required
          type="email"
        />

        <transition name="fade">
          <button
            v-if="email.length"
            class="notify-me__clear"
            type="button"
            @click="handleClear"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.clear')"
            />
            <icon-close />
          </button>
        </transition>
      </div>
    </div>

    <p
      v-if="state === 'invalid'"
      class="notify-me__error text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
      v-text="$string('product.notify_me.error_email')"
    />

    <p
      v-else-if="state === 'error'"
      class="notify-me__error text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
      v-text="$string('product.notify_me.error_generic')"
    />

    <div v-else-if="success" class="notify-me__success">
      <icon-check />

      <p
        class="text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
        v-text="$string('product.notify_me.success_title')"
      />

      <p
        class="text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        v-text="$string('product.notify_me.success_body')"
      />
    </div>
  </div>
</template>

<script>
/**
 * Vue: Notify me (notify-me)
 * -----------------------------------------------------------------------------
 * Notify me input for out of stock products.
 *
 * @param {String} sku - Variant SKU.
 *
 * @emits updateSuccess - Emit success state.
 *
 */
import IconCheck from '~icons/general/check.svg';
import IconClose from '~icons/directional-navigation/close.svg';
import { buildQueryString } from '~/core/query';

export default {
  name: 'NotifyMe',

  components: {
    IconCheck,
    IconClose,
  },

  props: {
    variantId: {
      type: Number,
      default: 0,
      required: true,
    },
    productId: {
      type: Number,
      default: 0,
      required: true,
    },
  },

  emits: {
    'update-success': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      email: '',
      state: 'ready',
      success: false,
      submittedVariantIds: [],
    };
  },

  watch: {
    /**
     * Watch email input to update state.
     */
    email() {
      this.state = 'ready';
    },

    /**
     * Watch VariantId to determine if VariantId has already been submitted.
     * Wait for next tick so submitted variants can be loaded from sessonStorage
     */
    async variantId() {
      await this.$nextTick();
      this.success = this.submittedVariantIds.includes(this.variantId);
    },

    /**
     * Watch success to emit update.
     */
    success() {
      this.$emit('update-success', this.success);
    },
  },

  mounted() {
    /**
     * Retrieve submitted SKUs.
     */
    const storedSubmittedVariantIds = sessionStorage.getItem('cnvs-notify-me');

    if (storedSubmittedVariantIds) {
      this.submittedVariantIds = JSON.parse(storedSubmittedVariantIds);
    }

    this.success = this.submittedVariantIds.includes(this.variantId);

    /**
     * Set default email.
     */
    if (this.$variable('customer.email')) {
      this.email = this.$variable('customer.email');
    }
  },

  methods: {
    /**
     * Handles form submission.
     * - Validates email on submission only, usually not required as browser
     *   validation will prevent form submission.
     * - Triggered from inside product form component.
     */
    handleNotifyMe() {
      return new Promise(async (resolve, reject) => {
        const emailRegex = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
        this.state = emailRegex.test(this.email) ? 'ready' : 'invalid';

        if (this.state !== 'ready') {
          resolve(false);
          return;
        }

        /**
         * Simulated success.
         */
        if (this.email === 'success@esn.de') {
          await cnvs.Sleep(1500);
          this.submitVariantId(this.variantId);
          this.state = 'ready';
          this.success = true;
          resolve(true);
          return;
        }

        /**
         * Simulated error.
         */
        if (this.email === 'error@esn.de') {
          await cnvs.Sleep(1500);
          this.state = 'error';
          this.success = false;
          resolve(false);
          return;
        }

        /**
         * Form submission.
         */

        const formData = {
          email: this.email,
          formId: 'back-in-stock',
          productId: this.productId,
          variantId: this.variantId,
          country: this.$variable('store.language').toUpperCase(),
          warehouse:
            this.$variable('store.marketCode').toUpperCase() === 'UK'
              ? 'UK'
              : 'RAW Warehouse',
        };

        const queryString = buildQueryString(formData);
        const baseUrl = `${window.location.protocol}//${window.location.host}/apps/custom-klaviyo/`;
        const apiUrl = `${baseUrl}?${queryString}`;

        // Fetch data from the Klaviyo API
        try {
          const response = await fetch(apiUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          });

          if (!response.ok) {
            throw new Error('Network response was not ok');
          }

          this.submitVariantId(this.variantId);
          this.state = 'ready';
          this.success = true;
          resolve(true);
        } catch (error) {
          this.state = 'error';
          this.success = false;
          // manually emit success state, because it didn't "change"
          this.$emit('update-success', this.success);
          cnvs.ReportError('Failed to submit notify me', error);
          reject(error);
        }
      });
    },

    submitVariantId(variantId) {
      this.submittedVariantIds.push(variantId);
      sessionStorage.setItem(
        'cnvs-notify-me',
        JSON.stringify(this.submittedVariantIds),
      );
    },

    handleClear() {
      this.email = '';
      this.$refs.input.focus();
    },
  },
};
</script>

<style lang="scss">
@import './notify-me';
</style>
